import React, { useState } from 'react';
import { X, QrCode, Loader2 } from 'lucide-react';

export default function GenerateQRDialog({ open, onClose, availableTables, onGenerate }) {
  const [selectedTable, setSelectedTable] = useState('');
  const [generating, setGenerating] = useState(false);

  if (!open) return null;

  const selectedTableObj = availableTables.find((t) => String(t.id) === String(selectedTable));

  const handleGenerate = async () => {
    if (!selectedTable) return;
    try {
      setGenerating(true);
      await onGenerate({ tableId: Number(selectedTable) });
      setSelectedTable('');
      onClose();
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setSelectedTable('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md mx-4 p-5">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 transition-colors"
        >
          <X size={16} className="text-slate-400" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-purple-100 rounded-lg">
            <QrCode size={18} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Generate QR Code</h3>
            <p className="text-xs text-slate-500">Select a table to generate a QR code for</p>
          </div>
        </div>

        {/* Table Select */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Table</label>
          {availableTables.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">All tables already have QR codes generated</p>
          ) : (
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
            >
              <option value="">Select a table...</option>
              {availableTables.map((table) => (
                <option key={table.id} value={table.id}>
                  {table.name} — {table.floorName}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* URL Preview */}
        {selectedTableObj && (
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1">URL Preview</label>
            <p className="text-xs text-slate-600 font-mono break-all">
              https://menu.maverickdine.com/qr/{selectedTableObj.name.toLowerCase().replace(/\s+/g, '-')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!selectedTable || generating}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <QrCode size={14} />
                Generate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
